import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import {
  Drawer,
  Box,
  Button,
  LinearProgress,
  Grid,
  Typography,
  Tabs,
  Tab,
  Divider,
} from "@material-ui/core";

import { useStyles } from "./styles";
import { useDockerManagementContext } from "../DockerManagementProvider";
import DeviceModeFormField from "./Fields/DeviceModeFormField";
import HardwareTypeFormField from "./Fields/HardwareTypeFormField";
import YamlSelectionFormField from "./Fields/YamlSelectionFormField";
import DeviceSelectionFormField from "./Fields/DeviceSelectionFormField";
import useCancellationToken from "../../../../hooks/useCancellationToken";
import { useConfirmationDialog } from "../../../../hooks/useConfirmationDialog";
import apiClient from "../../../../auth/apiClient";

function TabPanel({ children, index, tabValue }) {
  return tabValue === index ? <Box>{children}</Box> : null;
}

const EditorDrawer = () => {
  const classes = useStyles();

  const {
    state: { editObj, availableComposes },
    actions: { addNewConfiguration, updateConfiguration, cancelEdit, showError },
  } = useDockerManagementContext();


	const { showDialog } = useConfirmationDialog();

  const handleClose = () => {
    cancelEdit();
  };

  const { execute: updateConfig } = useCancellationToken({
    func: async function ({ values, cancelToken }) {
      const mappedValues = {
        ...values,
        deviceIds:
          values.deviceIds?.length > 0 ? values.deviceIds.map((d) => d.deviceId) : null,
      };

      if (mappedValues.id) {
        // Update existing
        const response = await apiClient.put(`docker-support`, mappedValues, {
          headers: { "Content-Type": "application/json" },
          cancelToken,
        });
        updateConfiguration(response.data);
      } else {
        // Create new
        const response = await apiClient.post(`docker-support`, mappedValues, {
          headers: { "Content-Type": "application/json" },
          cancelToken,
        });
        addNewConfiguration(response.data);
			}
			cancelEdit();
    },
    errHandleFunc: (err) => {
      showError("Failed to Update script configuration: " + err);
    },
  });

  const onFormSubmit = async (values) => {
    await updateConfig({ values });
  };

  const finalSchema = Yup.object().shape(
    {
      yamlFile: Yup.string()
        .required("yamlFile is required")
        .oneOf(availableComposes, "Invalid yamlFile value"),

      useDeviceIds: Yup.boolean(),

      mode: Yup.number()
        .nullable(true)
        .when(["useDeviceIds", "chipset"], {
          is: (useDeviceIds, chipset) => !useDeviceIds && chipset !== null,
          then: (schema) => schema.required("Mode is required when chipset is set"),
          otherwise: (schema) => schema.nullable(),
        })
        .when("useDeviceIds", {
          is: true,
          then: (schema) =>
            schema.oneOf([null], "Mode must be null when using deviceIds"),
        }),

      chipset: Yup.number()
        .nullable(true)
        .when(["useDeviceIds", "mode"], {
          is: (useDeviceIds, mode) => !useDeviceIds && mode !== null,
          then: (schema) => schema.required("Chipset is required when mode is set"),
          otherwise: (schema) => schema.nullable(),
        })
        .when("useDeviceIds", {
          is: true,
          then: (schema) =>
            schema.oneOf([null], "Chipset must be null when using deviceIds"),
        }),

      deviceIds: Yup.array()
        .of(
          Yup.object().shape({
            deviceId: Yup.string().required("Device ID is required"),
          })
        )
        .when("useDeviceIds", {
          is: true,
          then: (schema) =>
            schema.min(
              1,
              "At least one deviceId is required when chipset and mode are not set"
            ),
        }),
    },
    [["chipset", "mode"]]
  );

  const formProvider = useForm({
    resolver: yupResolver(finalSchema),
    defaultValues: {
      chipset: null,
      mode: null,
      yamlFile: "",
      useDeviceIds: !!editObj?.useDeviceIds,
      deviceIds: Array.isArray(editObj?.deviceIds) ? editObj.deviceIds : [],
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    control,
    setValue,
		getValues,
    watch,
		trigger,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = formProvider;

  const useDeviceIds = watch("useDeviceIds");

  const onTabChange = async (event, newValue) => {
		const values = getValues();

		if (newValue == values.useDeviceIds) {
			return;
		}
		
		if(
			!newValue && (Array.isArray(values.deviceIds) && values.deviceIds.length > 0)
		) {
			let continueChange = await showDialog({
				title: `Are you sure you want to switch modes?`,
				message: "Any configuration in the current device list tab will be lost, are you sure you want to switch modes?",
				buttons: [
					{ name: "Yes", color: "primary" },
					{ name: "Cancel", color: "secondary" },
				],
			});

			if (continueChange !== "Yes") return;
		} else if (newValue && (values.chipset != 0 || values.mode != 0)) {

			let continueChange = await showDialog({
				title: `Are you sure you want to switch modes?`,
				message: "Any configuration in the current config tab will be lost, are you sure you want to switch modes?",
				buttons: [
					{ name: "Yes", color: "primary" },
					{ name: "Cancel", color: "secondary" },
				],
			});

			if (continueChange !== "Yes") return;
		}

    setValue("useDeviceIds", !!newValue);

    if (newValue) {
      setValue("chipset", null);
      setValue("mode", null);
      setValue("deviceIds", []);
    } else {
      setValue("chipset", 0);
      setValue("mode", 0);
      setValue("deviceIds", []);
    }
  };

  useEffect(() => {
    if (editObj) {
      reset({
        ...editObj,
        useDeviceIds: !!editObj.useDeviceIds,
        deviceIds: Array.isArray(editObj.deviceIds) ? editObj.deviceIds : [],
      });
    }
  }, [editObj, reset]);

  return (
    <Drawer
      anchor="right"
      open={!!editObj}
      onClose={handleClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box display="flex" flexDirection="column" height="100%">

        <Box className={classes.headerContainer}>
          <Typography variant="h6" gutterBottom color="primary">
            {editObj?.id ? "Edit" : "Add"} Docker Configuration
          </Typography>
        </Box>
        <Divider className={classes.divider} />

        <Tabs
          value={useDeviceIds ? 1 : 0}
          onChange={async (event, newValue) => await onTabChange(event, !!newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabList}
        >
          <Tab key={0} label="Config" />
          <Tab key={1} label="Device List" />
        </Tabs>

        <Box className={classes.contentContainer}>
          <FormProvider {...formProvider}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <TabPanel index={0} tabValue={useDeviceIds ? 1 : 0}>
                <Grid container spacing={4}>
                  <Grid item xs={12} className={classes.fieldItem}>
                    <DeviceModeFormField control={control} errors={errors} />
                  </Grid>
                  <Grid item xs={12} className={classes.fieldItem}>
                    <HardwareTypeFormField control={control} errors={errors} />
                  </Grid>
                  <Grid item xs={12} className={classes.fieldItem}>
                    <YamlSelectionFormField control={control} errors={errors} />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel index={1} tabValue={useDeviceIds ? 1 : 0}>
                <Grid container spacing={4}>
                  <Grid item xs={12} className={classes.fieldItem} >
                    <DeviceSelectionFormField control={control} errors={errors} trigger={trigger} />
                  </Grid>
                  <Grid item xs={12} className={classes.fieldItem} >
                    <YamlSelectionFormField control={control} errors={errors} />
                  </Grid>
                </Grid>
              </TabPanel>

              {isSubmitting && (
                <Box mt={2}>
                  <LinearProgress color="primary" />
                </Box>
              )}
            </form>
          </FormProvider>
        </Box>

        <Box p={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Box mx={1}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || !isValid || !isDirty}
              onClick={handleSubmit(onFormSubmit)}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EditorDrawer;
